import React, {useCallback, useEffect, useState} from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import Svg, { Path } from 'react-native-svg';
import CustomDrawer from './CustomDrawer';
import Header from './Header';
import BottomNavigationBar from './BottomNavigationBar';
import AIChatBot from './AIChatBot';
import {storeSetting, ShowToast} from '../Common/common';
import {resetState} from '../redux/services/authSlice';
import {useDispatch} from 'react-redux';
import {useSignOutMutation} from '../redux/services/ausersApi';

const HEADER_HEIGHT = 56;
const BOTTOM_NAV_HEIGHT = 60;

const AIIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path
      d="M9.6 4.6a2 2 0 1 1 1.4 3.4H2M12.6 19.4a2 2 0 1 0 1.4-3.4H2M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const DrawerLayout = ({children, navigationRef, onNavigationStateChange}: any) => {
  const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [canGoBack, setCanGoBack] = useState(false);
  const [chatBotOpen, setChatBotOpen] = useState(false);
  const [signOut] = useSignOutMutation();
  const toggleDrawer = () => setDrawerOpen(prev => !prev);
  const closeDrawer = () => setDrawerOpen(false);

  // Reactively update canGoBack whenever navigation state changes.
  // Called by Home.tsx wiring NavigationContainer's onStateChange to this.
  const updateCanGoBack = useCallback(() => {
    if (navigationRef?.current) {
      try {
        setCanGoBack(Boolean(navigationRef.current.canGoBack()));
      } catch {
        setCanGoBack(false);
      }
    }
  }, [navigationRef]);

  // Expose the updater so Home.tsx can call it via onNavigationStateChange
  useEffect(() => {
    if (onNavigationStateChange) {
      onNavigationStateChange(updateCanGoBack);
    }
  }, [onNavigationStateChange, updateCanGoBack]);

  // Also run once after mount in case ref is already ready
  useEffect(() => {
    const timer = setTimeout(updateCanGoBack, 100);
    return () => clearTimeout(timer);
  }, [updateCanGoBack]);

  const handleBack = () => {
    if (navigationRef?.current) {
      if (navigationRef.current.canGoBack()) {
        navigationRef.current.goBack();
      } else {
        // Fallback: navigate to root Dashboard instead of silently doing nothing
        navigationRef.current.navigate('Dashboard');
      }
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Header
          showBack={canGoBack}
          onMenuPress={toggleDrawer}
          onBackPress={handleBack}
          onNotificationPress={() =>
            navigationRef?.current?.navigate?.('Notifications')
          }
          onLogoutPress={async () => {
            try {
              // Call signout API to log user logout in BlueHMS system
              await signOut({}).unwrap();
            } catch (error) {
              // Continue with logout even if API call fails
              console.error('Signout API error:', error);
            } finally {
              // Clear all stored data
              try {
                // Clear settings
                await storeSetting('defoutlet', '0');
                // Pause auto-login on manual logout so user stays on Login screen
                await storeSetting('autologin_paused', 'true');

                // Clear token from storage
                if (Platform.OS === 'web') {
                  localStorage.removeItem('token');
                  localStorage.removeItem('authData');
                  localStorage.removeItem('multiTab.tabs');
                  localStorage.removeItem('multiTab.current');

                  // Clear sessionStorage keys starting with "report_"
                  const keysToRemove: string[] = [];
                  for (let i = 0; i < sessionStorage.length; i++) {
                    const key = sessionStorage.key(i);
                    if (key && key.startsWith('report_')) {
                      keysToRemove.push(key);
                    }
                  }
                  keysToRemove.forEach(key => sessionStorage.removeItem(key));
                } else {
                  // React Native: clear AsyncStorage
                  await AsyncStorage.multiRemove([
                    'token',
                    'authData',
                    'multiTab.tabs',
                    'multiTab.current',
                  ]);

                  // Clear report_ keys
                  const allKeys = await AsyncStorage.getAllKeys();
                  const reportKeys = allKeys.filter(key =>
                    key.startsWith('report_'),
                  );
                  if (reportKeys.length > 0) {
                    await AsyncStorage.multiRemove(reportKeys);
                  }
                }
              } catch (storageError) {
                console.error('Error clearing storage:', storageError);
              }

              // Reset auth state
              dispatch(resetState());

              // Show logout message
              ShowToast('Logged out successfully');
            }
          }}
        />
      </View>

      {/* Content */}
      <View style={styles.content}>{children}</View>

      {/* Bottom Nav */}
      <View style={styles.bottomNav}>
        <BottomNavigationBar navigation={navigationRef} navigationRef={navigationRef} />
      </View>

      {/* Floating AI Button */}
      <TouchableOpacity
        style={styles.floatingAIButton}
        onPress={() => setChatBotOpen(true)}
        activeOpacity={0.8}
      >
        <AIIcon size={28} color="#fff" />
      </TouchableOpacity>

      {/* AI ChatBot Modal */}
      <AIChatBot open={chatBotOpen} onClose={() => setChatBotOpen(false)} />

      {/* Drawer */}
      {drawerOpen && (
        <>
          <View style={styles.drawer}>
            <CustomDrawer
              navigation={navigationRef}
              onClose={closeDrawer}
            />
          </View>
          <TouchableOpacity style={styles.backdrop} onPress={closeDrawer} />
        </>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    backgroundColor: '#fff',
  },
  header: {
    height: HEADER_HEIGHT,
    zIndex: 10,
    backgroundColor: '#fff',
    elevation: 4,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    backgroundColor: '#fff',
    zIndex: 1,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  bottomNav: {
    height: BOTTOM_NAV_HEIGHT,
    backgroundColor: '#fff',
    elevation: 4,
    zIndex: 10,
    flexShrink: 0,
  },
  drawer: {
    position: 'absolute',
    top: HEADER_HEIGHT,
    bottom: BOTTOM_NAV_HEIGHT,
    left: 0,
    width: 240,
    backgroundColor: '#fff',
    zIndex: 20,
    elevation: 6,
  },
  backdrop: {
    position: 'absolute',
    top: HEADER_HEIGHT,
    bottom: BOTTOM_NAV_HEIGHT,
    left: 240,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    zIndex: 15,
  },
  floatingAIButton: {
    position: 'absolute',
    bottom: BOTTOM_NAV_HEIGHT + 16,
    right: 16,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#2563eb',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    zIndex: 11,
  },
});

export default DrawerLayout;

